import React, { Component, FunctionComponent, useState } from "react"
import { locale, throttle } from "@albelli/photoselector"
import { RowLayout, ContainerListView, Layout } from "@albelli/list-view-react"
import { SelectionManager, SelectionSortingOptions } from "../Stores/selection-manager"
import { ImagePreview } from "./Preview"
import { PhotoItem } from "../../item"
import sortingIcon from "../Graphic/sorting-icon.svg"
import closeIcon from "../Graphic/close-black.svg"
import { DropDownItem, DropDown } from "./DropDown"

interface SelectionPreviewProps {
  store: SelectionManager
  onSelect: () => void
  range?: { max: number; min: number }
  shouldSort?: boolean
  showSelection?: boolean
  selectItemsText?: string
  optionalButtons?: []
}

interface SelectionPreviewStates {
  showPreview: boolean
  selectionSize: number
  items: PhotoItem[]
  showRangeMessage: boolean
}

export class SelectionPreview extends Component<SelectionPreviewProps, SelectionPreviewStates> {
  private readonly layout: Layout
  private updateItemsThrottled: () => void

  constructor(props: SelectionPreviewProps) {
    super(props)
    this.layout = new RowLayout(5)
    this.updateItemsThrottled = throttle(this.updateItems, 500)
    this.state = { showPreview: false, selectionSize: 0, items: [], showRangeMessage: false }
  }

  componentDidMount() {
    this.setState({ items: this.getItems() })
    this.props.shouldSort && this.props.store.setSortingOption("datetaken_oldnew")
    this.props.store.addListener(this.updateItemsThrottled)
  }

  componentWillUnmount() {
    this.props.store.removeListener(this.updateItemsThrottled)
  }

  togglePreview = () => {
    this.setState({ showPreview: !this.state.showPreview })
  }

  updateItems = () => {
    const items = this.getItems()
    const showPreview = items.length === 0 ? false : this.state.showPreview
    const { range } = this.props
    const showRangeMessage = this.isOutOfRange(range, items.length)
    this.setState({ items, showPreview, showRangeMessage })
  }

  getItems(): PhotoItem[] {
    return this.props.store.getSelectedItems()
  }

  /*
   ** getItems directly uses store methods which will do sorting before returning the list
   ** hence, storing the result in state.items and returing reference to it
   ** with this it saves some computation time
   */
  getStateItems = () => {
    return this.state.items
  }

  removeItem(item: PhotoItem) {
    this.props.store.deselect(item)
  }

  handleSortingChange = (sortingOption: SelectionSortingOptions) => {
    this.props.store.setSortingOption(sortingOption)
    this.updateItems()
  }

  handleRangeMessageClose = () => {
    this.setState({ showRangeMessage: false })
  }

  handleSelect = () => {
    const { range, onSelect } = this.props
    const { items } = this.state
    if (this.isOutOfRange(range, items.length)) {
      this.setState({ showRangeMessage: true })
    } else {
      onSelect()
    }
  }

  handleClick = (callback) => {
    const { items } = this.state
    callback(items)
  }

  isOutOfRange(range: { max: number; min: number } | undefined, size: number): boolean {
    const overMin = range && range.min && range.min > size
    const overMax = range && range.max && range.max < size
    return !!(overMin || overMax) // so typescript will not complain about types
  }

  renderPhotoItem(photo: PhotoItem) {
    const style = { backgroundImage: `url(${photo.urlThumbnail})` }
    return (
      <div className="photoselector-selection-list__item" id={photo.id} style={style}>
        <i className="photoselector-remove-from-selection-icon" data-tam="remove-from-selection-icon" onClick={() => this.removeItem(photo)}></i>
        <ImagePreview store={this.props.store} item={photo} getItems={this.getStateItems} />
      </div>
    )
  }

  renderList(items: PhotoItem[]) {
    return (
      <div className="photoselector-selection-preview-wrapper">
        <ContainerListView className="photoselector-selection-list" items={items} layout={this.layout}>
          {(item) => this.renderPhotoItem(item as PhotoItem)}
        </ContainerListView>
      </div>
    )
  }

  render() {
    const { showPreview, items, showRangeMessage } = this.state
    const { range, showSelection, shouldSort, selectItemsText, optionalButtons } = this.props
    const selectionSize = items.length

    return (
      <div className={`photoselector-select-list-container ${showPreview ? " expand" : ""}`} data-tam="add-photos-container">
        {range && showRangeMessage ? <OutOfRangeMessage range={range} onClose={this.handleRangeMessageClose} /> : null}
        <div className="preview-container">
          {showSelection && selectionSize > 0 ? (
            <div className="photoselector-select-options">
              <SelectionCount onToggle={this.togglePreview} showPreview={showPreview} selectionSize={selectionSize} />
            </div>
          ) : null}
          {showSelection && shouldSort && selectionSize > 0 ? (
            <div className="photoselector-select-options">
              <div className="photoselector-selection-pipe">|</div>
            </div>
          ) : null}
          {shouldSort && selectionSize > 0 ? (
            <div className="photoselector-select-options">
              <SelectionSort onSortingChange={this.handleSortingChange} />
            </div>
          ) : null}
          <AddPhotos disabled={this.isOutOfRange(range, selectionSize)} onSelect={this.handleSelect} text={selectItemsText} />
          {optionalButtons && optionalButtons.length
            ? optionalButtons.map((btn, index) => {
                return (
                  <div className={`button ${btn["className"]}`} key={index} onClick={this.handleClick.bind(this, btn["handler"])}>
                    {btn["text"]}
                  </div>
                )
              })
            : null}
        </div>
        {showSelection && selectionSize > 0 ? this.renderList(items) : null}
      </div>
    )
  }
}

const OutOfRangeMessage: FunctionComponent<{ range: { max: number; min: number }; onClose: () => void }> = ({ range, onClose }) => {
  const { rangeText } = locale.get()

  let text = ""
  if (range.min && range.max) {
    text = rangeText.minMax(range.min, range.max)
  } else if (range.min) {
    text = rangeText.min(range.min)
  } else if (range.max) {
    text = rangeText.max(range.max)
  }

  return (
    <div className="photoselector-range-container">
      <img className="photoselector-close-icon" src={closeIcon} onClick={onClose} />
      <div className="photoselector-range-container-title">{locale.get().addMorePhotos}</div>
      <div className="photoselector-range-container-message">{text}</div>
    </div>
  )
}

const AddPhotos: FunctionComponent<{ disabled: boolean; onSelect: () => void; text?: string }> = ({ disabled, onSelect, text }) => {
  const doneButtonClass = "photoselector-done-button" + (disabled ? " disabled" : "")
  return (
    <div className={doneButtonClass} onClick={onSelect} data-tam="add-photos-button">
      {text || locale.get().generic.done}
    </div>
  )
}

const SelectionCount: FunctionComponent<{ onToggle: () => void; showPreview: boolean; selectionSize: number }> = ({
  onToggle,
  showPreview,
  selectionSize
}) => {
  const dropStyle = { transform: showPreview ? "rotate(0deg)" : "rotate(-90deg)" }
  return (
    <div className="photoselector-selector-count" onClick={onToggle}>
      <div className="photoselector-selector-triangle" style={dropStyle} data-tam="selected-triangle" />
      <div className="photoselector-selections-link-text" data-tam="selected-text">
        <b> {selectionSize} </b> {locale.get().generic.selected}
      </div>
    </div>
  )
}

interface SelectionSortProps {
  onSortingChange: (sortingOption: SelectionSortingOptions) => void
}

const SelectionSort: React.FunctionComponent<SelectionSortProps> = ({ onSortingChange }) => {
  const { generic } = locale.get()

  const sortOptions: DropDownItem[] = [
    { id: "datetaken_oldnew", title: generic.oldestfirst },
    { id: "datetaken_newold", title: generic.newestfirst },
    { id: "filename_az", title: generic.az },
    { id: "filename_za", title: generic.za }
  ]

  const [selected, setSelected] = useState<DropDownItem>(sortOptions[0])

  const onSelect = (item: DropDownItem) => {
    setSelected(item)
    onSortingChange(item.id as SelectionSortingOptions)
  }

  return (
    <DropDown list={sortOptions} selectedId={selected.id} onSelect={onSelect} position="top" className="preview-sort">
      <div className="photoselector-selector-sort">
        <img className="photoselector-dropdown-icon" src={sortingIcon} />
        <span className="photoselector-sort-value photoselector-sort-option">
          <span className="photoselector-sort-choosen">{generic.sortBy}:</span>
          <span className="hide-on-mobile">{selected.title}</span>
        </span>
      </div>
    </DropDown>
  )
}
